/*!
 * Copyright © 2020-2021. Verizon Connect Ireland Limited. All rights reserved.
 */

import { InfoBoxOptions, IPlotConfig } from '../../models';

import { ECombinedPlotType, EPlotIcon, EPlotType } from '../../../core/models';
import { trackableClusterOptions } from './map-cluster-configs.constants';

export const defaultLabelInfoBoxOptions: InfoBoxOptions = {
  content: '',
  boxClass: 'info-box',
  closeBoxURL: '',
  enableEventPropagation: false,
  disableAutoPan: true,
  zIndex: 10,
  plotMargin: -25
};

const vehiclePlotConfig: IPlotConfig = {
  type: EPlotType.vehicle,
  size: { height: 28, width: 28 },
  offset: { height: 13, width: 14 },
  canBeFittedToMap: true,
  zIndex: new Map([
    [EPlotIcon.Stopped, 3],
    [EPlotIcon.Idle, 4],
    [EPlotIcon.MovingE, 5],
    [EPlotIcon.MovingN, 5],
    [EPlotIcon.MovingNE, 5],
    [EPlotIcon.MovingNW, 5],
    [EPlotIcon.MovingS, 5],
    [EPlotIcon.MovingSE, 5],
    [EPlotIcon.MovingSW, 5],
    [EPlotIcon.MovingW, 5],
    [EPlotIcon.CustomerCare, 4],
    [EPlotIcon.LostSignal, 4],
    [EPlotIcon.Panic, 4],
    [EPlotIcon.Private, 4],
    [EPlotIcon.Towing, 4],
    [EPlotIcon.Charging0, 2],
    [EPlotIcon.Charging10, 2],
    [EPlotIcon.Charging20, 2],
    [EPlotIcon.Charging30, 2],
    [EPlotIcon.Charging40, 2],
    [EPlotIcon.Charging50, 2],
    [EPlotIcon.Charging60, 2],
    [EPlotIcon.Charging70, 2],
    [EPlotIcon.Charging80, 2],
    [EPlotIcon.Charging90, 2],
    [EPlotIcon.Charging99, 2]
  ])
};

const vehicleWithIssuePlotConfig: IPlotConfig = {
  ...vehiclePlotConfig,
  type: ECombinedPlotType.vehicleWithIssue,
  size: { height: 46, width: 46 },
  offset: { height: 23, width: 13 },
  canBeFittedToMap: true,
  zIndex: new Map([
    [`${EPlotIcon.Stopped}-ic-${EPlotIcon.DeviceWithIssue}`, 3],
    [`${EPlotIcon.Idle}-ic-${EPlotIcon.DeviceWithIssue}`, 4],
    [`${EPlotIcon.MovingE}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.MovingN}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.MovingNE}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.MovingNW}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.MovingS}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.MovingSE}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.MovingSW}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.MovingW}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.CustomerCare}-ic-${EPlotIcon.DeviceWithIssue}`, 4],
    [`${EPlotIcon.LostSignal}-ic-${EPlotIcon.DeviceWithIssue}`, 4],
    [`${EPlotIcon.Panic}-ic-${EPlotIcon.DeviceWithIssue}`, 4],
    [`${EPlotIcon.Private}-ic-${EPlotIcon.DeviceWithIssue}`, 4],
    [`${EPlotIcon.Towing}-ic-${EPlotIcon.DeviceWithIssue}`, 4],
    [`${EPlotIcon.Charging}-ic-${EPlotIcon.DeviceWithIssue}`, 2]
  ])
};

const chargingVehicleWithIssuePlotConfig: IPlotConfig = {
  ...vehiclePlotConfig,
  type: ECombinedPlotType.chargingDeviceWithIssue,
  size: { height: 46, width: 46 },
  offset: { height: 26, width: 16 },
  canBeFittedToMap: true,
  zIndex: new Map([
    [`${EPlotIcon.Charging0}-ic-${EPlotIcon.DeviceWithIssue}`, 2],
    [`${EPlotIcon.Charging10}-ic-${EPlotIcon.DeviceWithIssue}`, 2],
    [`${EPlotIcon.Charging20}-ic-${EPlotIcon.DeviceWithIssue}`, 2],
    [`${EPlotIcon.Charging30}-ic-${EPlotIcon.DeviceWithIssue}`, 2],
    [`${EPlotIcon.Charging40}-ic-${EPlotIcon.DeviceWithIssue}`, 2],
    [`${EPlotIcon.Charging50}-ic-${EPlotIcon.DeviceWithIssue}`, 2],
    [`${EPlotIcon.Charging60}-ic-${EPlotIcon.DeviceWithIssue}`, 2],
    [`${EPlotIcon.Charging70}-ic-${EPlotIcon.DeviceWithIssue}`, 2],
    [`${EPlotIcon.Charging80}-ic-${EPlotIcon.DeviceWithIssue}`, 2],
    [`${EPlotIcon.Charging90}-ic-${EPlotIcon.DeviceWithIssue}`, 2],
    [`${EPlotIcon.Charging99}-ic-${EPlotIcon.DeviceWithIssue}`, 2]
  ])
};

const poweredAssetPlotConfig: IPlotConfig = {
  ...vehiclePlotConfig,
  type: EPlotType.poweredAsset,
  zIndex: new Map([
    [EPlotIcon.StoppedPoweredAsset, 3],
    [EPlotIcon.IdlePoweredAsset, 4],
    [EPlotIcon.MovingEPoweredAsset, 5],
    [EPlotIcon.MovingNPoweredAsset, 5],
    [EPlotIcon.MovingNEPoweredAsset, 5],
    [EPlotIcon.MovingNWPoweredAsset, 5],
    [EPlotIcon.MovingSPoweredAsset, 5],
    [EPlotIcon.MovingSEPoweredAsset, 5],
    [EPlotIcon.MovingSWPoweredAsset, 5],
    [EPlotIcon.MovingWPoweredAsset, 5],
    [EPlotIcon.CustomerCarePoweredAsset, 4],
    [EPlotIcon.LostSignalPoweredAsset, 4],
    [EPlotIcon.TowingPoweredAsset, 4]
  ])
};

const poweredAssetWithIssuePlotConfig: IPlotConfig = {
  ...poweredAssetPlotConfig,
  type: ECombinedPlotType.poweredAssetWithIssue,
  size: { height: 46, width: 46 },
  offset: { height: 23, width: 13 },
  canBeFittedToMap: true,
  zIndex: new Map([
    [`${EPlotIcon.StoppedPoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 3],
    [`${EPlotIcon.IdlePoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 4],
    [`${EPlotIcon.MovingEPoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.MovingNPoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.MovingNEPoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.MovingNWPoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.MovingSPoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.MovingSEPoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.MovingSWPoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.MovingWPoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 5],
    [`${EPlotIcon.CustomerCarePoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 4],
    [`${EPlotIcon.LostSignalPoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 4],
    [`${EPlotIcon.TowingPoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 4]
  ])
};

const hotSpotPlotConfig: IPlotConfig = {
  type: EPlotType.hotspot,
  canBeFittedToMap: false,
  size: { height: 28, width: 28 },
  offset: { height: 13, width: 14 },
  zIndex: new Map([[EPlotIcon.Hotspot, 0]])
};

const geofencePlotConfig: IPlotConfig = {
  type: EPlotType.geofence,
  canBeFittedToMap: false,
  size: { height: 28, width: 28 },
  offset: { height: 13, width: 14 },
  zIndex: new Map([[EPlotIcon.Geofence, 0]])
};

const garminPlotConfig: IPlotConfig = {
  type: EPlotType.garmin,
  canBeFittedToMap: true,
  size: { height: 28, width: 28 },
  offset: { height: 13, width: 14 },
  zIndex: new Map([
    [EPlotIcon.GarminStopCompleted, 1],
    [EPlotIcon.GarminStopEnRoute, 1],
    [EPlotIcon.GarminStopPeding, 1]
  ])
};

const workOrderPLotConfig: IPlotConfig = {
  type: EPlotType.workOrder,
  canBeFittedToMap: false,
  size: { height: 24, width: 24 },
  offset: { height: 12, width: 12 },
  zIndex: 2
};

const harshDrivingEventPlotConfig: IPlotConfig = {
  type: EPlotType.harshDrivingEvent,
  canBeFittedToMap: true,
  size: { height: 40, width: 26 },
  offset: { height: 37, width: 13 }
};

const assetPlotConfig: IPlotConfig = {
  type: EPlotType.asset,
  canBeFittedToMap: true,
  size: { height: 28, width: 28 },
  offset: { height: 13, width: 14 },
  zIndex: new Map([
    [EPlotIcon.LocationCheckInNonPoweredAsset, 3],
    [EPlotIcon.StoppedNonPoweredAsset, 3],
    [EPlotIcon.MovingNENonPoweredAsset, 5]
  ])
};

const assetWithIssuePlotConfig: IPlotConfig = {
  ...assetPlotConfig,
  type: ECombinedPlotType.assetWithIssue,
  size: { height: 46, width: 46 },
  offset: { height: 23, width: 13 },
  canBeFittedToMap: true,
  zIndex: new Map([
    [`${EPlotIcon.StoppedNonPoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 3],
    [`${EPlotIcon.LocationCheckInNonPoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 3],
    [`${EPlotIcon.MovingNENonPoweredAsset}-ic-${EPlotIcon.DeviceWithIssue}`, 5]
  ])
};

//#region Replay icon z index
// We define z indexes for icons on the replay map
// These will control which icons take precedence when hovering on segments/icons have violations
const replayZIndex = [
  [EPlotIcon.Stopped, 6],
  [EPlotIcon.Idle, 6],
  [EPlotIcon.MovingE, 5],
  [EPlotIcon.MovingN, 5],
  [EPlotIcon.MovingNE, 5],
  [EPlotIcon.MovingNW, 5],
  [EPlotIcon.MovingS, 5],
  [EPlotIcon.MovingSE, 5],
  [EPlotIcon.MovingSW, 5],
  [EPlotIcon.MovingW, 5],
  [EPlotIcon.ReplayStart, 8],
  [EPlotIcon.Private, 6],
  [EPlotIcon.StoppedPoweredAsset, 6],
  [EPlotIcon.IdlePoweredAsset, 6],
  [EPlotIcon.MovingEPoweredAsset, 5],
  [EPlotIcon.MovingNPoweredAsset, 5],
  [EPlotIcon.MovingNEPoweredAsset, 5],
  [EPlotIcon.MovingNWPoweredAsset, 5],
  [EPlotIcon.MovingSPoweredAsset, 5],
  [EPlotIcon.MovingSEPoweredAsset, 5],
  [EPlotIcon.MovingSWPoweredAsset, 5],
  [EPlotIcon.MovingWPoweredAsset, 5],
  [EPlotIcon.MovingNENonPoweredAsset, 5],
  [EPlotIcon.StoppedNonPoweredAsset, 5],
  [EPlotIcon.LocationCheckInNonPoweredAsset, 5],
  [EPlotIcon.Charging, 7]
];
// Each of the above base plots should have grey, violation, and grey violation variants with the same zindex
const replayGreyZIndex = replayZIndex.map(v => [`${v[0]}-grey` as EPlotIcon, v[1]]);
const replayViolationZIndex = replayZIndex.map(v => [`${v[0]}-${EPlotIcon.Violation}` as EPlotIcon, v[1]]);
const replayGreyViolationZIndex = replayGreyZIndex.map(v => [`${v[0]}-${EPlotIcon.Violation}-grey` as EPlotIcon, v[1]]);
// These misc plots do not fit the above rule (should not have variants/variants have differing z indexes)
const replayMiscZIndex = [
  [EPlotIcon.StopPin, 8],
  [EPlotIcon.IdlePin, 8],
  [EPlotIcon.MovingPin, 8],
  [EPlotIcon.MultidayPin, 8],
  [EPlotIcon.ChargingPin, 8],
  [EPlotIcon.MultidayArrow, 6],
  [EPlotIcon.MultidayArrowActive, 7],
  [EPlotIcon.ReplayEnd, 8],
  [`${EPlotIcon.ReplayEnd}-${EPlotIcon.Violation}`, 8],
  [`${EPlotIcon.ReplayEnd}-grey-${EPlotIcon.Violation}`, 7],
  [`${EPlotIcon.ReplayEnd}-grey-${EPlotIcon.Violation}`, 7]
];
//#endregion

const replayPlotConfig: IPlotConfig = {
  type: EPlotType.replay,
  size: { height: 28, width: 28 },
  offset: { height: 13, width: 14 },
  canBeFittedToMap: true,
  zIndex: new Map<EPlotIcon, number>(
    [...replayZIndex, ...replayGreyZIndex, ...replayViolationZIndex, ...replayGreyViolationZIndex, ...replayMiscZIndex].map(
      x => x as [EPlotIcon, number]
    )
  )
};

const trackableClusterConfig: IPlotConfig = {
  type: EPlotType.cluster,
  size: {
    height: trackableClusterOptions.styles[0].height,
    width: trackableClusterOptions.styles[0].width
  },
  offset: {
    height: trackableClusterOptions.styles[0].height / 2,
    width: trackableClusterOptions.styles[0].width / 2
  },
  canBeFittedToMap: true
};

const polylineDotConfig: IPlotConfig = {
  type: EPlotType.polylineDayDot,
  size: {
    height: 10,
    width: 10
  },
  offset: {
    height: 5,
    width: 5
  },
  canBeFittedToMap: true,
  zIndex: new Map([[EPlotIcon.PolylineDot, 3]]),
  infoBoxOptions: {
    ...defaultLabelInfoBoxOptions,
    boxClass: 'info-box info-box-dark',
    plotMargin: -15
  }
};

export const plotTypeConfigs: IPlotConfig[] = [
  vehiclePlotConfig,
  vehicleWithIssuePlotConfig,
  chargingVehicleWithIssuePlotConfig,
  hotSpotPlotConfig,
  geofencePlotConfig,
  garminPlotConfig,
  workOrderPLotConfig,
  harshDrivingEventPlotConfig,
  assetPlotConfig,
  assetWithIssuePlotConfig,
  replayPlotConfig,
  poweredAssetPlotConfig,
  poweredAssetWithIssuePlotConfig,
  trackableClusterConfig,
  polylineDotConfig
];
